import { useQuery } from "@tanstack/react-query";
import { erc20Abi, formatEther, formatUnits } from "viem";
import { useOverrides } from "../../../shared/hooks/use-overrides";
import { formatBalance } from "../../../shared/utils/formatters";
import { useWalletInfo } from "../../wallet/hooks/use-wallet-info";
import { getPublicClient } from "../../wallet/utils/config";

// Utility function to parse balance override
function parseBalanceOverride(override: string): number | null {
  try {
    const parsed = Number.parseFloat(override);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

// Fetch native balance using cached public client
async function fetchNativeBalanceFromRpc(address: string, chainId: string): Promise<number> {
  try {
    // Get the cached public client for this chain
    const nonCaip2ChainId = chainId.replace("eip155:", "");
    const publicClient = getPublicClient(Number(nonCaip2ChainId));

    // Get balance using viem's getBalance method
    const balanceWei = await publicClient.getBalance({
      address: address as `0x${string}`,
    });

    // Convert from wei to ether using viem's formatEther
    const balanceInEther = formatEther(balanceWei);
    return Number.parseFloat(balanceInEther);
  } catch (error) {
    console.error("Failed to fetch balance from RPC:", error);
    throw error;
  }
}

async function fetchTokenBalanceFromRpc(
  userAddress: string,
  tokenAddress: string,
  decimals: number,
  chainId: string,
): Promise<number> {
  try {
    // Get the cached public client for this chain
    const nonCaip2ChainId = chainId.replace("eip155:", "");
    const publicClient = getPublicClient(Number(nonCaip2ChainId));

    // First, check if the contract exists and has code
    const contractCode = await publicClient.getBytecode({
      address: tokenAddress as `0x${string}`,
    });

    if (!contractCode || contractCode === "0x") {
      console.warn(`Contract does not exist at address ${tokenAddress} on chain ${chainId}`);
      return 0;
    }

    // Get token balance using viem's readContract method with ERC-20 ABI
    const balanceWei = await publicClient.readContract({
      address: tokenAddress as `0x${string}`,
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [userAddress as `0x${string}`],
    });

    // Convert from wei to token units using viem's formatUnits
    const balanceInTokens = formatUnits(balanceWei, decimals);
    return Number.parseFloat(balanceInTokens);
  } catch (error) {
    console.error("Failed to fetch token balance from RPC:", {
      error,
      tokenAddress,
      userAddress,
      chainId,
      decimals,
    });
    // Return 0 instead of throwing to prevent the query from failing
    return 0;
  }
}

interface UseBalanceParams {
  tokenAddress?: string;
  decimals?: number;
  symbol?: string;
  chainId?: string; // Optional chain ID to override the connected wallet's chain
}

export function useBalance({
  tokenAddress,
  decimals,
  symbol,
  chainId: overrideChainId,
}: UseBalanceParams = {}) {
  const { isConnected, address, chainId: walletChainId } = useWalletInfo();
  const { getOverride } = useOverrides();

  // Use override chain ID if provided, otherwise use wallet's chain ID
  const chainId = overrideChainId || walletChainId?.toString();

  // Get override state
  const balanceOverride = getOverride("balance");
  const hasOverride = balanceOverride?.enabled ?? false;
  const overrideValue = hasOverride ? balanceOverride?.value : null;
  const parsedOverrideValue =
    hasOverride && balanceOverride?.value ? parseBalanceOverride(balanceOverride.value) : null;

  const isTokenBalance =
    !!tokenAddress &&
    tokenAddress !== "0x0000000000000000000000000000000000000000" &&
    !!decimals &&
    !!symbol;
  const invalidTokenBalanceReq =
    (!!tokenAddress && !decimals) ||
    (!!tokenAddress && !symbol) ||
    (!tokenAddress && !!decimals && !!symbol);

  const getQueryKey = () => {
    if (isTokenBalance) {
      return [
        "token-balance",
        address,
        chainId,
        hasOverride ? overrideValue : null,
        tokenAddress,
        decimals,
        symbol,
      ];
    }
    return ["native-balance", address, chainId, hasOverride ? overrideValue : null];
  };

  const query = useQuery({
    queryKey: getQueryKey(),
    queryFn: async (): Promise<string> => {
      // Handle override case first
      if (hasOverride && parsedOverrideValue !== null) {
        return formatBalance(parsedOverrideValue);
      }

      // Ensure we have required wallet info
      if (!address || !chainId || invalidTokenBalanceReq) return "0";

      try {
        const balanceValue = isTokenBalance
          ? await fetchTokenBalanceFromRpc(address, tokenAddress, decimals, chainId)
          : await fetchNativeBalanceFromRpc(address, chainId);
        return formatBalance(balanceValue);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
        return "0";
      }
    },
    enabled: isConnected && !!address && !!chainId && !invalidTokenBalanceReq,
    staleTime: hasOverride ? 0 : 10000,
    refetchInterval: hasOverride ? false : 10000,
  });

  return {
    data: query.data ?? "0", // Guarantee string type
    isLoading: query.isLoading,
    error: query.error,
  };
}
